import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import LanguageToggle from '../../components/LanguageToggle';
import VocationWorksLogo from '../../components/VocationWorksLogo';

const NAVY     = '#0D1B2A';
const AMBER    = '#F5A623';
const WHITE    = '#FFFFFF';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

export default function OrganizationLogin() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleLogin = async () => {
    setError('');
    setSubmitting(true);
    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    setSubmitting(false);
    if (signInError) { setError(signInError.message); return; }
    // Membership is verified on the dashboard itself (same defense-in-depth pattern used
    // by the other role dashboards), so this page only needs to authenticate.
    navigate('/organization/dashboard');
  };

  const handleForgotPassword = async () => {
    if (!email) { alert(t('auth.login.enterEmailFirst')); return; }
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + '/reset-password',
    });
    if (resetError) alert(resetError.message);
    else alert(t('auth.login.resetEmailSent'));
  };

  useEffect(() => {
    const link = document.createElement('link');
    link.rel  = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);

    const style = document.createElement('style');
    style.textContent = `
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { background-color: #F5F6F8; }
      .org-login-input::placeholder { color: rgba(0,0,0,0.3); }
      @media (max-width: 767px) {
        .org-login-content { align-items: flex-start !important; padding-left: 16px !important; padding-right: 16px !important; }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(link);
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div style={{ fontFamily: FONT_BODY, minHeight: '100dvh', backgroundColor: '#F5F6F8', display: 'flex', flexDirection: 'column' }}>

      {/* Navbar */}
      <nav style={{
        height: '60px', backgroundColor: NAVY,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 2rem', flexShrink: 0,
      }}>
        <div onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
          <VocationWorksLogo variant="reversed" size={36} />
        </div>
        <LanguageToggle />
      </nav>

      {/* Main content */}
      <div className="org-login-content" style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem 1.25rem',
      }}>
        <div style={{
          backgroundColor: WHITE, borderRadius: '16px',
          boxShadow: '0 4px 24px rgba(13,27,42,0.10)',
          padding: '40px', width: '100%', maxWidth: '400px',
        }}>
          <h1 style={{ fontFamily: FONT_BODY, fontSize: '24px', fontWeight: 700, color: NAVY, marginBottom: '6px' }}>
            {t('organization.login.heading')}
          </h1>
          <p style={{ fontSize: '0.9rem', color: 'rgba(13,27,42,0.5)', marginBottom: '28px' }}>
            {t('organization.login.subheading')}
          </p>

          <form onSubmit={e => { e.preventDefault(); handleLogin(); }}>
            <div style={{ marginBottom: '18px' }}>
              <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: NAVY, marginBottom: '6px' }}>
                {t('auth.login.emailLabel')}
              </label>
              <input
                className="org-login-input"
                type="email"
                placeholder={t('organization.login.emailPlaceholder')}
                value={email}
                onChange={e => setEmail(e.target.value)}
                style={{
                  width: '100%', padding: '11px 14px', fontSize: '0.9375rem',
                  border: '1px solid #D1D5DB', borderRadius: '8px', outline: 'none',
                  fontFamily: FONT_BODY, color: NAVY, backgroundColor: WHITE,
                }}
              />
            </div>

            <div style={{ marginBottom: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                <label style={{ fontSize: '0.8125rem', fontWeight: 600, color: NAVY }}>{t('auth.login.passwordLabel')}</label>
                <span onClick={handleForgotPassword} style={{ fontSize: '0.8125rem', color: AMBER, fontWeight: 500, cursor: 'pointer' }}>
                  {t('auth.login.forgotPassword')}
                </span>
              </div>
              <input
                className="org-login-input"
                type="password"
                placeholder={t('auth.login.passwordPlaceholder')}
                value={password}
                onChange={e => setPassword(e.target.value)}
                style={{
                  width: '100%', padding: '11px 14px', fontSize: '0.9375rem',
                  border: '1px solid #D1D5DB', borderRadius: '8px', outline: 'none',
                  fontFamily: FONT_BODY, color: NAVY, backgroundColor: WHITE,
                }}
              />
            </div>

            {error && (
              <p style={{ fontSize: '0.82rem', color: '#DC2626', marginBottom: '14px' }}>{error}</p>
            )}

            <button
              type="submit"
              disabled={submitting}
              style={{
                width: '100%', padding: '13px', backgroundColor: AMBER, color: NAVY,
                border: 'none', borderRadius: '8px', fontFamily: FONT_BODY, fontSize: '0.9375rem',
                fontWeight: 700, cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.7 : 1,
                marginTop: '18px', marginBottom: '20px',
              }}
            >
              {submitting ? t('auth.login.signingIn') : t('auth.login.signInButton')}
            </button>
          </form>

          <p style={{ textAlign: 'center', fontSize: '0.82rem', color: 'rgba(13,27,42,0.45)', lineHeight: 1.5 }}>
            {t('organization.login.inviteOnlyNote')}
          </p>
        </div>
      </div>
    </div>
  );
}
